'use client';

import GenericPage, { IGenericPageProps } from '../../app/pages/ui/GenericPage';
import { SECURITY_API } from '../domain/api';
import { useApi } from '../../../data/hooks/useApi';
import { ISecurityCheckpointResponse } from '../domain/models/ISecurityCheckpointResponse';
import { getCurrentAirportCodeFromId } from '../../../components/common/navigation/utils/getCurrentAirportCode';
import RefreshData from '../../../components/common/refresh-data/RefreshData';
import { CheckpointType } from '../domain/models/CheckpointType';
import SecurityCardsShimmer from './SecurityCardsShimmer';
import ErrorMessage from '../../shared/components/error/ErrorMessage';
import { ErrorMessages } from '../../../utils/errorMessages';
import CheckpointsList from './CheckpointsList';
import { InfoCardPageContentWrap, InfoCardSectionTitle } from './SecurityPage';
import { IPoiItem } from '../../shop-dine-relax/domain/models/IPoiItem';
import { ISiteSectionEntry } from '../../../contentful/types/ISiteSection';
import { ICheckpointEntry } from '../../../contentful/types/ICheckpointEntry';
import { applyContentfulOverrides } from '../domain/utils/applyContentfulOverrides';

interface IInternationalProcessingPageProps extends IGenericPageProps {
	securityPoiData?: IPoiItem[];
	siteSection?: ISiteSectionEntry;
	contentfulCheckpoints?: ICheckpointEntry[];
}

export default function InternationalProcessingPage({
	page,
	securityPoiData,
	siteSection,
	contentfulCheckpoints,
}: IInternationalProcessingPageProps) {
	const airportCode = getCurrentAirportCodeFromId(
		page.fields.parent?.fields.id,
	);

	const { data, loading, error, refetch, lastUpdatedAt } =
		useApi<ISecurityCheckpointResponse>().useGetApi({
			url: `${SECURITY_API}/${airportCode}`,
			apiVersion: '120',
			pollingIntervalMs: 60000,
		});

	const checkpoints = data?.data.wait_times;

	const filteredCheckpoints = checkpoints?.filter((checkpoint) =>
		checkpoint.attributes?.includes(CheckpointType.FIS),
	);

	const mergedCheckpoints =
		filteredCheckpoints &&
		applyContentfulOverrides(filteredCheckpoints, contentfulCheckpoints);

	const hasOneCheckpoint = mergedCheckpoints?.length === 1;

	const securityTitle = `International Security ${
		hasOneCheckpoint ? 'Checkpoint' : 'Checkpoints'
	}`;

	const renderErrorMessage = () => {
		if (error || !data) {
			return <ErrorMessage message={ErrorMessages.GenericError} />;
		}

		if (mergedCheckpoints?.length === 0) {
			return (
				<ErrorMessage message={ErrorMessages.SecurityCheckpointsNotFound} />
			);
		}
		return null;
	};

	return (
		<GenericPage page={page} siteSection={siteSection}>
			<InfoCardPageContentWrap>
				<InfoCardSectionTitle>{securityTitle}</InfoCardSectionTitle>

				{loading && !error ? (
					<SecurityCardsShimmer />
				) : (
					renderErrorMessage() || (
						<CheckpointsList
							checkpoints={mergedCheckpoints}
							securityPoiData={securityPoiData}
						/>
					)
				)}

				{mergedCheckpoints?.length !== 0 && (
					<RefreshData
						label="Refresh Wait Times"
						refetch={refetch}
						lastUpdatedAt={lastUpdatedAt}
					/>
				)}
			</InfoCardPageContentWrap>
		</GenericPage>
	);
}
